import React, { useEffect, useState } from "react";
import { Text, View } from "native-base";
import { Image } from "react-native";
import { ChevronDown } from "lucide-react-native";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Poland from "../../../../assets/Icons/Flags/Poland";
import Russia from "../../../../assets/Icons/Flags/Russia";
import PressableWrapp from "../../../Universal/Buttons/PressableReact/PressableWrapp";
import { normalizeText } from "./helpers";
import { UnitFullV2Styles } from "./styles";

interface UnitFullV2ProductDescriptionProps {
  country?: string | null;
  description: unknown;
  imageUri?: string | null;
  isSmallVersion: boolean;
  scaleAll: number;
  styles: UnitFullV2Styles;
}

const resolveCountryFlag = (country: string) => {
  const normalized = country.toLowerCase();

  if (
    normalized.includes("польш") ||
    normalized.includes("poland") ||
    normalized.includes("polska")
  ) {
    return Poland;
  }

  if (normalized.includes("росси") || normalized.includes("russia")) {
    return Russia;
  }

  return null;
};

const UnitFullV2ProductDescription: React.FC<
  UnitFullV2ProductDescriptionProps
> = ({ country, description, imageUri, isSmallVersion, scaleAll, styles }) => {
  const text = normalizeText(description);
  const image = normalizeText(imageUri);
  const countryText = normalizeText(country);
  const CountryFlag = countryText ? resolveCountryFlag(countryText) : null;
  const [isOpen, setIsOpen] = useState(true);
  const [contentHeight, setContentHeight] = useState(0);
  const heightProgress = useSharedValue(1);
  const chevronProgress = useSharedValue(1);

  useEffect(() => {
    heightProgress.value = withTiming(isOpen ? 1 : 0, {
      duration: 280,
      easing: Easing.out(Easing.cubic),
    });
    chevronProgress.value = withTiming(isOpen ? 1 : 0, {
      duration: 240,
      easing: Easing.out(Easing.cubic),
    });
  }, [chevronProgress, heightProgress, isOpen]);

  const bodyAnimatedStyle = useAnimatedStyle(() => ({
    height: contentHeight * heightProgress.value,
    opacity: interpolate(heightProgress.value, [0, 1], [0.4, 1]),
    overflow: "hidden",
  }));

  const chevronAnimatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        rotate: `${interpolate(chevronProgress.value, [0, 1], [0, 180])}deg`,
      },
    ],
  }));

  if (!text) return null;

  return (
    <View style={styles.descriptionCard}>
      <PressableWrapp
        style={styles.descriptionHeader}
        onPress={() => setIsOpen((prev) => !prev)}
      >
        <Text style={styles.descriptionTitle}>Описание</Text>
        <Animated.View style={chevronAnimatedStyle}>
          <ChevronDown size={20 * scaleAll} color="#101010" />
        </Animated.View>
      </PressableWrapp>

      <Animated.View
        style={[styles.descriptionBodyAnimatedWrap, bodyAnimatedStyle]}
      >
        <View
          onLayout={(event) => {
            const nextHeight = event.nativeEvent.layout.height;
            if (nextHeight > 0 && nextHeight !== contentHeight) {
              setContentHeight(nextHeight);
            }
          }}
          style={[
            styles.descriptionBody,
            isSmallVersion ? styles.descriptionBodySmall : null,
          ]}
        >
          {!isSmallVersion && image ? (
            <View style={styles.descriptionImageWrap}>
              <Image
                source={{ uri: image }}
                resizeMode="contain"
                style={styles.descriptionImage}
              />
            </View>
          ) : null}

          <View style={styles.descriptionContent}>
            {countryText ? (
              <View style={styles.descriptionCountryRow}>
                {CountryFlag ? (
                  <View style={styles.descriptionCountryFlag}>
                    <CountryFlag size={30 * scaleAll} />
                  </View>
                ) : null}
                <View>
                  <Text style={styles.descriptionCountryLabel}>Страна</Text>
                  <Text style={styles.descriptionCountryValue}>
                    {countryText}
                  </Text>
                </View>
              </View>
            ) : null}

            <Text style={styles.descriptionText}>{text}</Text>
          </View>
        </View>
      </Animated.View>
    </View>
  );
};

export default React.memo(UnitFullV2ProductDescription);
